import React from 'react';
import {
  AccessTimeOutlined,
  AccountTreeOutlined,
  AssignmentTurnedInOutlined,
  BeachAccessOutlined,
  CalendarMonthOutlined,
  CelebrationOutlined,
  CenterFocusStrongOutlined,
  DashboardOutlined,
  DoneAllOutlined,
  FaceOutlined,
  GroupOutlined,
  HubOutlined,
  ListAltOutlined,
  PersonOutlined,
  ReceiptLongOutlined,
  SendOutlined,
  VerifiedUserOutlined,
} from '@mui/icons-material';

/**
 * ONE icon per destination, in ONE place — the web twin of
 * `svdy-mobile/src/components/navIcons.ts`, which has had this shape since 2026-06-24.
 *
 * Written after the owner found `RuleOutlined` on BOTH approval queues (2026-09-04). A census of the
 * 16 sidebar entries found 14 distinct icons, i.e. two collisions, and both were the same mistake:
 * the glyph was chosen at the call site, so two entries added at different times picked the same one
 * and nothing could notice (svdy-ui §17 — a value written at every call site will drift).
 *
 * The glyphs are the SEMANTIC counterparts of the phone's, so the same destination reads the same on
 * both clients. Exact glyph parity is impossible (Ionicons vs MUI), the MEANING is what carries:
 *
 *   mobile 'checkmark-done-outline'  → DoneAllOutlined         decided by me
 *   mobile 'time-outline'            → AccessTimeOutlined      an attendance edit
 *   mobile 'scan-outline'            → CenterFocusStrongOutlined  a photo to check
 *   mobile 'shield-checkmark'        → VerifiedUserOutlined    my own check outcomes
 *   mobile 'checkbox-outline'        → AssignmentTurnedInOutlined a step assigned to me
 *
 * Adding an entry means adding a line HERE. `workerNavIcons.test.ts` fails on a duplicate.
 */
/** Typed so a consumer can resize the glyph: the Overview card renders these at 14px while the
 *  sidebar uses MUI's `fontSize="small"`. Without the prop type, `cloneElement` widens to
 *  `Partial<unknown>` and rejects `sx`. */
export const NAV_ICON: Record<string, React.ReactElement<{ sx?: object; fontSize?: string }>> = {
  '/me/dashboard': <DashboardOutlined fontSize="small" />,
  // Attendance — the worker's own record.
  '/me/schedule': <CalendarMonthOutlined fontSize="small" />, // the month grid IS the roster
  '/me/time-entries': <ListAltOutlined fontSize="small" />, // the punch list
  '/me/leave': <BeachAccessOutlined fontSize="small" />, // matches the admin leave module
  '/me/holidays': <CelebrationOutlined fontSize="small" />, // company non-working days, NOT my roster
  '/me/clock-in-reviews': <VerifiedUserOutlined fontSize="small" />, // outcomes of my face punches
  // Requests — what I submitted.
  '/me/attendance-requests': <ReceiptLongOutlined fontSize="small" />,
  '/me/requests': <SendOutlined fontSize="small" />, // submitted and travelling
  // To-do — what is waiting on me.
  '/me/tasks': <AssignmentTurnedInOutlined fontSize="small" />, // a step I complete
  '/me/approvals': <DoneAllOutlined fontSize="small" />, // I decide
  '/me/attendance-approvals': <AccessTimeOutlined fontSize="small" />, // ... about time
  '/me/face-reviews': <CenterFocusStrongOutlined fontSize="small" />, // ... about a photo
  // Team / organization / account.
  '/me/team': <GroupOutlined fontSize="small" />,
  '/me/organization': <AccountTreeOutlined fontSize="small" />,
  '/me/org-chart': <HubOutlined fontSize="small" />,
  '/me/profile': <PersonOutlined fontSize="small" />,
  '/me/face-template': <FaceOutlined fontSize="small" />, // §14: the face glyph is for face surfaces
};

